// ChannelsSection.tsx — onglet « Chaînes » du Dashboard : les dernières chaînes créées
//
// Refait le 2026-10-17 d'après la maquette validée par l'utilisateur
// (https://claude.ai/artifact/P7Vxsauq28SpWX6pf6FfKE) : chiffres du haut (créées
// aujourd'hui, sur 7 jours, allumées, origine), filtres Origine / État, la plus récente
// en grand, puis les cartes par jour ; vue liste dense en bascule (retenue d'une visite
// à l'autre). Données : admin-api DashboardLatest (`channels` et `channelStats`).
//
// Super-admin (2026-10-17) : deux actions distinctes sur la chaîne d'un créateur,
// l'éteindre et la verrouiller (plus personne ne peut la rallumer tant qu'elle n'est
// pas déverrouillée). Bouton « ⋮ » des cartes et de la liste, boutons du grand
// encadré ; chaque action passe par une fenêtre de confirmation, qui montre aussi
// l'historique de la chaîne. Verrouiller exige un motif, envoyé par e-mail au créateur.
//
// « Régie » et « Fiche » ouvrent la chaîne dans la section Chaînes du bureau par
// l'évènement `regie:open-channel` (écouté par desktop.tsx), sans que ce composant
// ait à connaître la navigation.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './ChannelsSection.module.scss';
import { WWW_URLS } from '../../../backendServices/urls';
import { ChannelLock, ChannelModerationEvent, LOCK_REASON_MIN, getChannelModeration } from '../../../backendServices/broadcastingManagment/api_channelModeration';
import { Geo } from './ActivityOverview';
import { creatorPlace, CREATOR_PLACE_HINT, channelAnchorId } from './dashboardShared';
import { DeviceSession, devicesText, sessionSpan } from './ChannelDevicesPanel';

export type ChannelOrigin = 'regie' | 'ios' | 'android' | 'other' | 'unknown';

export interface DashboardChannel {
  id            : number;
  name          : string;
  publicRef     : string;
  slug          : string;
  hasThumbnail  : boolean;
  creationDate  : string | null;
  ephemeral     : boolean;
  channelStatus : string;
  streamSource  : string;
  publicPortal  : boolean;
  origin        : ChannelOrigin;
  city          : string | null;
  universe      : string | null;
  owner         : string;
  ownerEmail    : string;
  lock          : ChannelLock | null;
  geo?          : Geo | null;
  /** Smartphones connectés à sa régie pendant la période (2026-10-20). */
  devices?      : DeviceSession[];
  /** Pourquoi elle est listée (2026-10-23) : créée sur la période, ou seulement y a diffusé. */
  periodReason? : 'created' | 'broadcast';
  /** Chaîne supprimée (fiche créateur, Ressources, case « Montrer les chaînes supprimées »). */
  deleted?      : boolean;
  /** Date et heure de la suppression (chaîne supprimée seulement). */
  deletedAt?    : string | null;
  /** Un direct (Live) de cette chaîne a été créé aujourd'hui. */
  directToday?  : boolean;
}

/** Étiquette d'une chaîne listée sans avoir été créée sur la période. */
const PERIOD_REASON_LABEL: Record<string, string> = {
  broadcast: '📡 A diffusé sur la période',
};

export interface ChannelStats {
  today     : number;
  yesterday : number;
  week      : number;
  days      : { day: string; count: number }[];
  origins   : Record<'regie' | 'ios' | 'android' | 'other', number>;
  /** Directs (Live) créés aujourd'hui. */
  directToday? : number;
}

/** Bloc de chiffres sur lequel on a cliqué pour filtrer la liste, la carte et les villes. */
export type ChannelKpi = 'created' | 'direct' | 'week' | 'on' | null;

/** Ouvre la chaîne dans la section Chaînes du bureau : fiche (Tableau de bord) ou onglet Régie. */
export const OPEN_CHANNEL_EVENT = 'regie:open-channel';
export interface OpenChannelDetail { id: number; tab: 'fiche' | 'regie' }

interface Props {
  channels : DashboardChannel[];
  stats    : ChannelStats | null;
  loading  : boolean;
  error    : boolean;
  onPlay   : (channel: DashboardChannel) => void;
  onClearFilters?: () => void;
  filtered : boolean;
  /** Actions super-admin : `error` null si tout s'est bien passé ; `notice` = compte rendu à afficher. */
  onStop   : (channel: DashboardChannel) => Promise<ModerationOutcome>;
  onLock   : (channel: DashboardChannel, reason: string) => Promise<ModerationOutcome>;
  onUnlock : (channel: DashboardChannel, note: string) => Promise<ModerationOutcome>;
  /** Graphe, carte et villes de la période (ActivityOverview), placés sous les chiffres. */
  overview?: React.ReactNode;
  /** Chaînes du point désigné sur la carte : surlignées, la première amenée à l'écran. */
  picked?: DashboardChannel[];
  kpi?   : ChannelKpi;
  /** Éteindre / verrouiller : réservé au superadmin (faux pour un créateur). */
  canModerate? : boolean;
  onKpi? : (kpi: ChannelKpi) => void;
}

export interface ModerationOutcome { error: string | null; notice?: string }

const ACTION_LABEL: Record<string, string> = { stop: 'Éteinte', lock: 'Verrouillée', unlock: 'Déverrouillée' };

export type Moderation = 'stop' | 'lock' | 'unlock';

export const ON_STATUSES = [ 'active', 'restricted', 'activating', 'activating_restricted' ];
export const ORIGIN_LABEL: Record<ChannelOrigin, string> = { regie: 'Régie web', ios: 'iPhone', android: 'Android', other: 'Autre système', unknown: 'Origine inconnue' };
const VIEW_KEY = 'dashboardV2.channelsView';
const NEW_MS   = 3600_000;

// Couleur de repli d'une vignette sans mire : stable pour une chaîne donnée.
const TINTS = [ '#1E1B4B', '#164E63', '#7C2D12', '#3B0764', '#1E3A8A', '#831843', '#14532D', '#713F12' ];
export const tintOf = (id: number) => TINTS[id % TINTS.length];

type State = 'live' | 'on' | 'off';
export const stateOf = (c: DashboardChannel): State =>
  c.deleted || !ON_STATUSES.includes(c.channelStatus) ? 'off' : (c.streamSource && c.streamSource !== 'video' ? 'live' : 'on');

const initials = (name: string) =>
  name.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';

export const hour = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

export const dayLabel = (iso: string | null): string => {
  if (!iso) return 'Date inconnue';
  const today  = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(iso); target.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - target.getTime()) / 86_400_000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Hier';
  const label = target.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const ago = (iso: string | null): string => {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 24 * 60) return `il y a ${Math.round(minutes / 60)} h`;
  return dayLabel(iso).toLowerCase();
};

const ownerText = (c: DashboardChannel) =>
  c.owner && c.ownerEmail && c.owner !== c.ownerEmail ? c.owner : (c.owner || c.ownerEmail || 'Créateur inconnu');

export const placeOf = (c: DashboardChannel) => creatorPlace(c.geo) || c.city || '';

/** Les smartphones d'une chaîne, une ligne chacun, pour une infobulle. */
const devicesTitle = (c: DashboardChannel) =>
  (c.devices ?? []).map(d => `${d.name} · ${d.ip ?? 'IP inconnue'} · ${creatorPlace(d.geo) || 'lieu inconnu'} · ${sessionSpan(d)}`).join('\n');

export const portalLink = (c: DashboardChannel) => c.slug ? `${WWW_URLS.portal_front}/${c.slug}` : '';
export const isNew = (c: DashboardChannel) => !!c.creationDate && Date.now() - new Date(c.creationDate).getTime() < NEW_MS;

const openChannel = (id: number, tab: OpenChannelDetail['tab']) =>
  window.dispatchEvent(new CustomEvent<OpenChannelDetail>(OPEN_CHANNEL_EVENT, { detail: { id, tab } }));

// ── Petits morceaux ───────────────────────────────────────────────────────────

const LockIcon: React.FC<{ size?: number }> = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
);

export const Badge: React.FC<{ state: State; fresh?: boolean; locked?: boolean; deleted?: boolean; style?: React.CSSProperties }> = ({ state, fresh, locked, deleted, style }) => (
  <span className={styles.badges} style={style}>
    {deleted && <span className={`${styles.badge} ${styles.badgeOff}`}>SUPPRIMÉE</span>}
    {locked && <span className={`${styles.badge} ${styles.badgeLocked}`}><LockIcon />VERROUILLÉE</span>}
    {state === 'live' && <span className={`${styles.badge} ${styles.badgeLive}`}><span className={styles.dot}/>EN DIRECT</span>}
    {state === 'on'   && <span className={`${styles.badge} ${styles.badgeOn}`}>ALLUMÉE</span>}
    {state === 'off'  && !deleted && <span className={`${styles.badge} ${styles.badgeOff}`}>ÉTEINTE</span>}
    {fresh && <span className={`${styles.badge} ${styles.badgeNew}`}>NOUVELLE</span>}
  </span>
);

/** La mire d'attente de la chaîne ; à défaut, son nom sur une couleur. */
export const Thumb: React.FC<{ c: DashboardChannel; big?: boolean }> = ({ c, big }) => {
  const [ failed, setFailed ] = useState(false);
  const src = c.publicRef ? `${WWW_URLS.MediaServer}${c.publicRef}/images/pending.webp` : '';
  return (
    <div className={styles.thumb} style={{ background: tintOf(c.id) }}>
      {src && !failed
        ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
        : <span className={big ? styles.thumbNameBig : styles.thumbName}>{c.name}</span>}
      {c.hasThumbnail && c.publicRef &&
        <img className={styles.thumbLogo} src={`${WWW_URLS.MediaServer}${c.publicRef}/images/thumbnail.webp`} alt="" loading="lazy" />}
      {/* position forcée : `td .badges` la rend statique quand les cartes sont dans une cellule (fiche créateur, Ressources) */}
      <Badge state={stateOf(c)} fresh={isNew(c)} locked={!!c.lock} deleted={!!c.deleted} style={{ position: 'absolute' }} />
      {!big && <span className={styles.thumbTime}>{hour(c.creationDate)}</span>}
    </div>
  );
};

/** Le menu « ⋮ » des actions super-admin, refermé par un clic ailleurs ou Échap. */
const ModerationMenu: React.FC<{ c: DashboardChannel; onModerate: (c: DashboardChannel, m: Moderation) => void }> = ({ c, onModerate }) => {
  const [ open, setOpen ] = useState(false);
  const [ pos , setPos  ] = useState<React.CSSProperties>({});
  const ref     = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Le menu est posé sur le document (portail, position fixe) : dans une liste ou une fiche à défilement,
  // le conteneur le coupait (première chaîne de l'onglet Ressources). Il s'ouvre vers le haut, ou vers
  // le bas quand il n'y a pas la place au-dessus.
  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      const right = Math.max(8, window.innerWidth - r.right);
      setPos(r.top > 130 ? { right, top: 'auto', bottom: window.innerHeight - r.top + 6 } : { right, bottom: 'auto', top: r.bottom + 6 });
    }
    setOpen(o => !o);
  };
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && !(menuRef.current && menuRef.current.contains(t))) setOpen(false);
    };
    const esc   = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const shut  = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc);
                   window.removeEventListener('scroll', shut, true); window.removeEventListener('resize', shut); };
  }, [open]);
  const choose = (m: Moderation) => { setOpen(false); onModerate(c, m); };
  return (
    <div className={styles.menuWrap} ref={ref}>
      <button type="button" className={styles.secondary} aria-haspopup="menu" aria-expanded={open}
              aria-label={`Actions super-admin sur ${c.name}`} onClick={toggle}>⋮</button>
      {open && createPortal(
        <div className={styles.menu} role="menu" ref={menuRef} style={{ position: 'fixed', zIndex: 5000, ...pos }}>
          <button type="button" role="menuitem" disabled={stateOf(c) === 'off'} onClick={() => choose('stop')}>
            Éteindre la chaîne{stateOf(c) === 'off' ? ' (déjà éteinte)' : ''}
          </button>
          {c.lock
            ? <button type="button" role="menuitem" onClick={() => choose('unlock')}>Déverrouiller la chaîne</button>
            : <button type="button" role="menuitem" className={styles.menuDanger} onClick={() => choose('lock')}>Verrouiller la chaîne…</button>}
        </div>,
        document.body
      )}
    </div>
  );
};

export const Actions: React.FC<{ c: DashboardChannel; onPlay: (c: DashboardChannel) => void; big?: boolean; canModerate?: boolean; onModerate: (c: DashboardChannel, m: Moderation) => void }> = ({ c, onPlay, big, canModerate = true, onModerate }) => c.deleted ? (
  <div className={big ? styles.actionsBig : styles.actions}><span className={styles.secondary} style={{ opacity: 0.6 }}>Supprimée</span></div>
) : (
  <div className={big ? styles.actionsBig : styles.actions}>
    <button type="button" className={styles.primary} disabled={c.ephemeral}
            title={c.ephemeral ? 'Chaîne pilotée par un autre système' : undefined}
            onClick={() => openChannel(c.id, 'regie')}>{big ? 'Ouvrir la régie' : 'Régie'}</button>
    <button type="button" className={styles.secondary} onClick={() => openChannel(c.id, 'fiche')}>{big ? 'Fiche de la chaîne' : 'Fiche'}</button>
    {/* Bouton « Regarder » (triangle) masqué sur demande (2026-10-28) : onPlay reste câblé pour le remettre. */}
    {portalLink(c) &&
      <a className={styles.secondary} href={portalLink(c)} target="_blank" rel="noreferrer" aria-label={`${c.name} sur Kleon.tv`}>
        {big ? 'Kleon.tv ↗' : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8"/></svg>}
      </a>}
    {!canModerate ? null : big
      ? <>
          <button type="button" className={styles.danger} disabled={stateOf(c) === 'off'} onClick={() => onModerate(c, 'stop')}>Éteindre</button>
          {c.lock
            ? <button type="button" className={styles.secondary} onClick={() => onModerate(c, 'unlock')}><LockIcon size={13} /> Déverrouiller</button>
            : <button type="button" className={styles.danger} onClick={() => onModerate(c, 'lock')}><LockIcon size={13} /> Verrouiller</button>}
        </>
      : <ModerationMenu c={c} onModerate={onModerate} />}
  </div>
);

const lockLine = (l: ChannelLock) =>
  `Verrouillée${l.by ? ` par ${l.by}` : ''}${l.at ? ` le ${new Date(l.at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}` : ''}${l.reason ? ` — ${l.reason}` : ''}`;

/**
 * Les actions super-admin d'une chaîne (éteindre, verrouiller, déverrouiller) : la fenêtre de confirmation
 * avec l'historique de la chaîne, et le compte rendu qui suit. Partagé par l'onglet Chaînes du Dashboard
 * et par l'onglet Ressources de la fiche créateur.
 */
export const useChannelModeration = (
  onStop  : (channel: DashboardChannel) => Promise<ModerationOutcome>,
  onLock  : (channel: DashboardChannel, reason: string) => Promise<ModerationOutcome>,
  onUnlock: (channel: DashboardChannel, note: string) => Promise<ModerationOutcome>,
) => {
  // Fenêtre de confirmation des actions super-admin.
  const [ dialog, setDialog ] = useState<{ c: DashboardChannel; m: Moderation } | null>(null);
  const [ reason, setReason ] = useState('');
  const [ busy  , setBusy   ] = useState(false);
  const [ failed, setFailed ] = useState('');
  const [ notice, setNotice ] = useState('');
  // Historique de la chaîne ouverte dans la fenêtre (null = en cours de lecture).
  const [ history, setHistory ] = useState<ChannelModerationEvent[] | null>(null);
  const moderate = (c: DashboardChannel, m: Moderation) => {
    setDialog({ c, m }); setReason(''); setFailed(''); setHistory(null);
    getChannelModeration(c.id).then(st => setHistory(st?.history ?? []));
  };
  const reasonOk = dialog?.m !== 'lock' || reason.trim().length >= LOCK_REASON_MIN;
  const confirm = async () => {
    if (!dialog || !reasonOk) return;
    setBusy(true); setFailed('');
    const out = dialog.m === 'stop' ? await onStop(dialog.c) : dialog.m === 'lock' ? await onLock(dialog.c, reason.trim()) : await onUnlock(dialog.c, reason.trim());
    setBusy(false);
    if (out.error) setFailed(out.error);
    else { setDialog(null); setNotice(out.notice ?? ''); }
  };

  const dialogElement = (
    <>
      {dialog && (
        <div className={styles.dialogOverlay} onMouseDown={() => !busy && setDialog(null)}>
          <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="channel-moderation-title" onMouseDown={e => e.stopPropagation()}>
            <h3 id="channel-moderation-title">
              {dialog.m === 'stop' ? 'Éteindre la chaîne ?' : dialog.m === 'lock' ? 'Verrouiller la chaîne ?' : 'Déverrouiller la chaîne ?'}
            </h3>
            <p className={styles.dialogName}>{dialog.c.name} <span className={styles.muted}>· {ownerText(dialog.c)}</span></p>
            {dialog.m === 'stop' && <p>
              Le direct s'arrête pour tous les spectateurs, et les caméras et la régie du créateur sont déconnectées.
              {dialog.c.lock ? ' La chaîne est verrouillée : le créateur ne pourra pas la rallumer.' : ' Le créateur pourra la rallumer, sauf si vous la verrouillez.'}
            </p>}
            {dialog.m === 'lock' && <>
              <p>
                Plus personne ne pourra allumer la chaîne (régie, apps, enregistrement) tant qu'un super-admin ne l'aura pas déverrouillée.
                {stateOf(dialog.c) !== 'off' ? " Verrouiller n'éteint pas : la chaîne reste allumée, éteignez-la ensuite si besoin." : ''}
              </p>
              <label className={styles.dialogLabel} htmlFor="channel-lock-reason">
                Motif <strong>(obligatoire)</strong> : envoyé par e-mail au créateur, montré quand il essaie d'allumer la chaîne, et gardé dans l'historique.
              </label>
              <textarea id="channel-lock-reason" className={styles.dialogText} rows={4} maxLength={500} value={reason} required
                        aria-invalid={!reasonOk}
                        placeholder="Par exemple : diffusion de contenus protégés par le droit d'auteur sans autorisation (match de football du 17 octobre)."
                        onChange={e => setReason(e.target.value)} />
              <span className={reasonOk ? styles.dialogHint : styles.dialogHintWarn}>
                {reasonOk ? `${reason.trim().length} / 500` : `Encore ${LOCK_REASON_MIN - reason.trim().length} caractère${LOCK_REASON_MIN - reason.trim().length > 1 ? 's' : ''} au moins`}
              </span>
              <p className={styles.muted}>
                Le créateur ({dialog.c.ownerEmail || 'adresse inconnue'}) reçoit un e-mail : sa chaîne ne respecte pas les conditions générales d'utilisation, avec ce motif.
              </p>
            </>}
            {dialog.m === 'unlock' && <>
              {dialog.c.lock && <p className={styles.muted}>{lockLine(dialog.c.lock)}</p>}
              <p>Le créateur pourra de nouveau allumer sa chaîne. Elle ne se rallume pas d'elle-même. Aucun e-mail n'est envoyé.</p>
              <label className={styles.dialogLabel} htmlFor="channel-unlock-note">Note pour l'historique (facultative)</label>
              <textarea id="channel-unlock-note" className={styles.dialogText} rows={2} maxLength={500} value={reason}
                        placeholder="Par exemple : le créateur a retiré les contenus en cause" onChange={e => setReason(e.target.value)} />
            </>}
            <div className={styles.historyBox}>
              <span className={styles.dialogLabel}>Historique de la chaîne</span>
              {history === null
                ? <span className={styles.muted}>Lecture…</span>
                : history.length === 0
                  ? <span className={styles.muted}>Aucune action d'un super-admin sur cette chaîne pour l'instant.</span>
                  : <ol className={styles.history}>
                      {history.map((h, i) => (
                        <li key={i}>
                          <span className={styles.historyWhen}>{new Date(h.at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          <span><strong>{ACTION_LABEL[h.action] ?? h.action}</strong>{h.by ? ` par ${h.by}` : ''}{h.reason ? ` — ${h.reason}` : ''}</span>
                          {h.action === 'lock' && <span className={h.emailSent ? styles.historyOk : styles.historyWarn}>
                            {h.emailSent ? `E-mail envoyé à ${h.emailTo}` : `E-mail non envoyé${h.emailTo ? ` à ${h.emailTo}` : ''}`}
                          </span>}
                        </li>
                      ))}
                    </ol>}
            </div>
            {failed && <p className={styles.dialogError}>{failed}</p>}
            <div className={styles.dialogActions}>
              <button type="button" className={styles.secondary} disabled={busy} onClick={() => setDialog(null)}>Annuler</button>
              <button type="button" className={dialog.m === 'unlock' ? styles.primary : styles.dangerSolid} disabled={busy || !reasonOk} onClick={confirm}>
                {busy
                  ? (dialog.m === 'stop' ? 'Extinction… (une quinzaine de secondes)' : 'Un instant…')
                  : (dialog.m === 'stop' ? 'Éteindre' : dialog.m === 'lock' ? 'Verrouiller et prévenir le créateur' : 'Déverrouiller')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return { moderate, notice, setNotice, dialogElement };
};

// ── Composant ────────────────────────────────────────────────────────────────

// Chaîne d'un point désigné sur la carte (bleu des chaînes créées depuis la régie web).
const PICKED    : React.CSSProperties = { outline: '2px solid #5D97F1', outlineOffset: 2 };
const PICKED_ROW: React.CSSProperties = { background: 'rgba(44,125,225,0.22)', boxShadow: 'inset 4px 0 0 #2C7DE1' };

const ChannelsSection: React.FC<Props> = ({ channels, stats, loading, error, onPlay, onClearFilters, filtered, onStop, onLock, onUnlock, overview, picked, kpi = null, onKpi, canModerate = true }) => {
  const pickedIds = new Set((picked ?? []).map(c => c.id));
  const listRef   = useRef<HTMLDivElement | null>(null);
  const pickedSig = (picked ?? []).map(c => c.id).join(',');
  useEffect(() => {
    if (pickedSig) listRef.current?.querySelector('[data-picked]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [pickedSig]);
  const [ origin, setOrigin ] = useState<'all' | 'regie' | 'ios' | 'android'>('all');
  const [ state,  setState  ] = useState<'all' | 'on' | 'off' | 'locked'>('all');
  const { moderate, notice, setNotice, dialogElement } = useChannelModeration(onStop, onLock, onUnlock);
  const [ view,   setView   ] = useState<'cards' | 'list'>(() => {
    // Vue en liste par défaut (2026-10-23) ; les cartes seulement si on les a choisies.
    try { return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'list'; } catch { return 'list'; }
  });
  const chooseView = (next: 'cards' | 'list') => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* navigation privée */ }
  };

  const shown = useMemo(() => channels.filter(c =>
    (origin === 'all' || c.origin === origin) &&
    (state === 'all' || (state === 'locked' ? !!c.lock : state === 'on' ? stateOf(c) !== 'off' : stateOf(c) === 'off'))
  ), [channels, origin, state]);

  const onCount   = channels.filter(c => stateOf(c) !== 'off').length;
  const liveCount = channels.filter(c => stateOf(c) === 'live').length;
  // Vue en cartes : toutes de la même taille (la plus récente n'est plus mise en grand).

  const groups = useMemo(() => {
    const out: { title: string; items: DashboardChannel[] }[] = [];
    for (const c of shown) {
      const title = dayLabel(c.creationDate);
      const g = out.find(x => x.title === title) ?? (out.push({ title, items: [] }), out[out.length - 1]);
      g.items.push(c);
    }
    return out;
  }, [shown]);

  const maxDay = Math.max(1, ...(stats?.days.map(d => d.count) ?? [1]));
  const originTotal = stats ? Math.max(1, stats.origins.regie + stats.origins.ios + stats.origins.android + stats.origins.other) : 1;
  const delta = stats ? stats.today - stats.yesterday : 0;

  return (
    <div className={styles.root} ref={listRef}>

      {/* ── Chiffres ── */}
      <div className={`${styles.kpis} ${styles.kpis5}`}>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'created' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'created'}
                title="Cliquer pour ne montrer que ces chaînes (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'created' ? null : 'created')}>
          <span className={styles.kpiLabel}>Chaînes créées aujourd'hui</span>
          <span className={styles.kpiValue}>{stats?.today ?? '–'}</span>
          {stats && <span className={delta >= 0 ? styles.kpiUp : styles.kpiMuted}>
            {delta === 0 ? 'autant qu’hier' : `${delta > 0 ? '+' : ''}${delta} par rapport à hier`}</span>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'direct' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'direct'}
                title="Cliquer pour ne montrer que les chaînes de ces directs (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'direct' ? null : 'direct')}>
          <span className={styles.kpiLabel}>Direct créé aujourd'hui</span>
          <span className={styles.kpiValue}>{stats?.directToday ?? '–'}</span>
          <span className={styles.kpiMuted}>{stats?.directToday ? 'lancés depuis minuit' : 'aucun depuis minuit'}</span>
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'week' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'week'}
                title="Cliquer pour ne montrer que les chaînes créées sur 7 jours (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'week' ? null : 'week')}>
          <span className={styles.kpiLabel}>Sur 7 jours</span>
          <span className={styles.kpiValue}>{stats?.week ?? '–'}</span>
          {stats && <span className={styles.spark} style={{ display: 'flex' }} aria-hidden="true">
            {stats.days.map((d, i) => <span key={d.day} className={i === stats.days.length - 1 ? styles.sparkToday : undefined}
                                            style={{ height: `${Math.max(3, Math.round(d.count / maxDay * 22))}px` }} title={`${d.day} : ${d.count}`} />)}
          </span>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'on' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'on'}
                title="Cliquer pour ne montrer que les chaînes allumées (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'on' ? null : 'on')}>
          <span className={styles.kpiLabel}>Allumées parmi ces chaînes</span>
          <span className={styles.kpiValue}>{onCount}</span>
          <span className={liveCount > 0 ? styles.kpiLive : styles.kpiMuted}>{liveCount > 0 ? `dont ${liveCount} en direct` : 'aucune en direct'}</span>
        </button>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Origine (7 jours)</span>
          {stats && <>
            <div className={styles.originBar} aria-hidden="true">
              <span className={styles.oRegie}   style={{ width: `${stats.origins.regie   / originTotal * 100}%` }}/>
              <span className={styles.oIos}     style={{ width: `${stats.origins.ios     / originTotal * 100}%` }}/>
              <span className={styles.oAndroid} style={{ width: `${stats.origins.android / originTotal * 100}%` }}/>
              <span className={styles.oOther}   style={{ width: `${stats.origins.other   / originTotal * 100}%` }}/>
            </div>
            <div className={styles.originLegend}>
              <span><i className={styles.oRegie}/>Régie web {stats.origins.regie}</span>
              <span><i className={styles.oIos}/>iPhone {stats.origins.ios}</span>
              <span><i className={styles.oAndroid}/>Android {stats.origins.android}</span>
              {stats.origins.other > 0 && <span><i className={styles.oOther}/>Autre {stats.origins.other}</span>}
            </div>
          </>}
        </div>
      </div>

      {overview}

      {/* ── Filtres de l'onglet ── */}
      <div className={styles.toolbar}>
        <div role="group" aria-label="Origine" className={styles.segment}>
          {([ ['all', 'Toutes'], ['regie', 'Régie web'], ['ios', 'iPhone'], ['android', 'Android'] ] as const).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={origin === k} className={origin === k ? styles.segOn : undefined} onClick={() => setOrigin(k)}>{label}</button>
          ))}
        </div>
        <div role="group" aria-label="État" className={styles.segment}>
          {([ ['all', 'Toutes'], ['on', 'Allumées'], ['off', 'Éteintes'], ['locked', 'Verrouillées'] ] as const).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={state === k} className={state === k ? styles.segOn : undefined} onClick={() => setState(k)}>{label}</button>
          ))}
        </div>
        <span className={styles.spacer}/>
        <div role="group" aria-label="Affichage" className={styles.segment}>
          <button type="button" aria-label="Vue en cartes" aria-pressed={view === 'cards'} className={view === 'cards' ? styles.segOn : undefined} onClick={() => chooseView('cards')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
          </button>
          <button type="button" aria-label="Vue en liste" aria-pressed={view === 'list'} className={view === 'list' ? styles.segOn : undefined} onClick={() => chooseView('list')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
          </button>
        </div>
      </div>

      {notice && (
        <div className={styles.notice} role="status">
          <span>{notice}</span>
          <button type="button" className={styles.secondary} onClick={() => setNotice('')}>Fermer</button>
        </div>
      )}

      {error && <div className={styles.empty}>Chargement impossible.</div>}

      {!error && shown.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyText}>
            <strong>{loading ? 'Chargement…' : 'Aucune chaîne créée ou ayant diffusé sur cette période'}</strong>
            {!loading && <span>Élargissez la période ou retirez un filtre. Les nouvelles chaînes apparaissent ici d'elles-mêmes, relevées chaque minute.</span>}
          </div>
          {!loading && (filtered || origin !== 'all' || state !== 'all') &&
            <button type="button" className={styles.secondary} onClick={() => { setOrigin('all'); setState('all'); onClearFilters?.(); }}>Effacer les filtres</button>}
        </div>
      )}

      {/* ── Vue cartes ── */}
      {!error && view === 'cards' && shown.length > 0 && (
        <>
          {groups.map(g => (
            <section key={g.title} className={styles.day}>
              <h3 className={styles.dayTitle}>{g.title} <span className={styles.muted}>{g.items.length} chaîne{g.items.length > 1 ? 's' : ''}</span></h3>
              <div className={styles.cards}>
                {g.items.map(c => (
                  <article key={c.id} id={channelAnchorId(c.id)} data-picked={pickedIds.has(c.id) || undefined} style={pickedIds.has(c.id) ? PICKED : undefined}
                           className={`${isNew(c) ? `${styles.card} ${styles.cardNew}` : (stateOf(c) === 'live' ? `${styles.card} ${styles.cardLive}` : styles.card)} ${c.lock ? styles.cardLocked : ''}`}>
                    <Thumb c={c} />
                    <div className={styles.cardBody}>
                      <span className={styles.cardName} title={c.name}>{c.name}</span>
                      <div className={styles.owner}>
                        <span className={styles.avatarSmall}>{initials(ownerText(c))}</span>
                        <span className={styles.ownerSmall} title={c.ownerEmail}>{ownerText(c)}</span>
                      </div>
                      <div className={styles.chips}>
                        <span>{ORIGIN_LABEL[c.origin]}</span>
                        {c.deleted && c.deletedAt && <span>Supprimée {dayLabel(c.deletedAt)} {hour(c.deletedAt)}</span>}
                        {placeOf(c) && <span title={CREATOR_PLACE_HINT}>📍 {placeOf(c)}</span>}
                        {c.universe && <span>{c.universe}</span>}
                        {!!c.devices?.length && <span title={devicesTitle(c)}>📱 {devicesText(c.devices)}</span>}
                        {c.periodReason && PERIOD_REASON_LABEL[c.periodReason] && <span title="Chaîne créée avant la période, listée parce qu'elle y a diffusé">{PERIOD_REASON_LABEL[c.periodReason]}</span>}
                      </div>
                      {c.lock && <div className={styles.lockNote} title={lockLine(c.lock)}><LockIcon size={12} /> {lockLine(c.lock)}</div>}
                      <Actions c={c} onPlay={onPlay} canModerate={canModerate} onModerate={moderate} />
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {/* ── Vue liste ── */}
      {!error && view === 'list' && shown.length > 0 && (
        <div className={styles.tableBox}>
          <table className={styles.table}>
            <thead>
              <tr><th>Chaîne</th><th>Créateur</th><th>Créée</th><th>Origine</th><th title={CREATOR_PLACE_HINT}>Ville du créateur</th><th>Univers</th><th>Smartphones</th><th>État</th><th>Kleon.tv</th><th className={styles.right}>Actions</th></tr>
            </thead>
            <tbody>
              {shown.map(c => (
                <tr key={c.id} id={channelAnchorId(c.id)} data-picked={pickedIds.has(c.id) || undefined} style={pickedIds.has(c.id) ? PICKED_ROW : undefined}>
                  <td><div className={styles.cellName}><span className={styles.miniThumb} style={{ background: tintOf(c.id) }}/>{c.name}</div></td>
                  <td>{ownerText(c)}</td>
                  <td className={styles.nowrap}>{dayLabel(c.creationDate)} {hour(c.creationDate)}
                    {c.deleted && c.deletedAt && <div className={styles.muted}>Supprimée {dayLabel(c.deletedAt)} {hour(c.deletedAt)}</div>}</td>
                  <td>{ORIGIN_LABEL[c.origin]}</td>
                  <td>{placeOf(c) || '—'}</td>
                  <td>{c.universe ?? '—'}</td>
                  <td title={devicesTitle(c) || undefined}>{c.devices?.length ? devicesText(c.devices) : '—'}</td>
                  <td><Badge state={stateOf(c)} fresh={isNew(c)} locked={!!c.lock} deleted={!!c.deleted} /></td>
                  <td className={c.publicPortal ? styles.visible : styles.muted}>{c.publicPortal ? 'Visible' : 'Non listée'}</td>
                  <td className={styles.right}><Actions c={c} onPlay={onPlay} canModerate={canModerate} onModerate={moderate} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogElement}
    </div>
  );
};

export default ChannelsSection;
